import { ArrowDownRight, ArrowUpRight, Minus } from 'lucide-react';
import type { Delta } from '../analytics/analyticsFormat';
import { toneOf } from '../analytics/analyticsFormat';
import { ErrorNote, Note } from '../../components/ErrorNote';
import { type CardFace, figureFit } from './cardFace';
import { ColumnChart, KpiSpark, TrendChart } from './charts/SeriesChart';

/** Which way a figure moved, as the arrow every change on this screen carries. */
export function DeltaArrow({ direction, size = 13 }: { direction: Delta['direction']; size?: number }) {
  if (direction === 'up') return <ArrowUpRight size={size} />;
  if (direction === 'down') return <ArrowDownRight size={size} />;
  return <Minus size={size} />;
}

/** A change, in its tone: up is good, down is bad, and flat says so. */
export function DeltaChip({ delta, size = 13 }: { delta: Delta; size?: number }) {
  return (
    <span className={`db-delta ${toneOf(delta.direction)}`}>
      <DeltaArrow direction={delta.direction} size={size} />
      {delta.label}
    </span>
  );
}

/**
 * A card's body on the desktop grid, drawn from its face (see cardFace.ts).
 *
 * The face decides WHAT the card says; this decides how it looks at desktop
 * widths. The phone screen draws the same face its own way, which is the
 * point: the two can differ in layout and never in the figure.
 */
export function FaceBody({ face }: { face: CardFace }) {
  switch (face.kind) {
    case 'loading':
      return <div className="db-skel" aria-label="Loading" />;
    case 'failed':
      return <ErrorNote error={face.error} />;
    case 'refused':
      return <Note tone="warn">Couldn’t load this one: {face.message}</Note>;
    case 'unavailable':
      return <p className="db-unavailable">{face.message}</p>;
    case 'kpi':
      return (
        <>
          <div className="db-figure-fit">
            <div className="db-figure" style={figureFit(face.figure)}>
              {face.figure}
            </div>
          </div>
          {/* The change and the sparkline share a line; what it is measured
              against goes underneath. Inline, "vs previous 30 days" wrapped to
              three lines in a card this narrow and pushed the figure off the
              top of its own card. The note slot is sized for something as
              short as "-14%", so a dispatch card's money goes in it and its
              standing line goes under, the same shape — and it is rendered
              even when empty so the sparkline keeps the right-hand edge every
              other card puts it on. */}
          <div className="db-kpi-foot">
            {face.foot.kind === 'delta' ? (
              <DeltaChip delta={face.foot.delta} />
            ) : (
              <span className="db-kpi-note">{face.foot.text}</span>
            )}
            {face.spark ? <KpiSpark data={face.spark} /> : null}
          </div>
          {face.caption ? <p className="db-kpi-compare">{face.caption}</p> : null}
        </>
      );
    case 'chart':
      return face.line ? (
        <TrendChart data={face.chart} highlight={face.highlight} focusNote={face.note} />
      ) : (
        <ColumnChart data={face.chart} highlight={face.highlight} focusNote={face.note} />
      );
  }
}
